function once(scope, callback) {
  function next() {
    if (next.called) {
      return;
    }
    next.called = true;
    return callback(scope);
  }
  next.called = false;
  return next;
}
const DEFAULT_FINAL_HANDLER = () => Promise.resolve();
class Runner {
  /**
   * The array of middleware handlers to execute in sequence.
   */
  #middleware;
  /**
   * The current index position in the middleware pipeline.
   */
  #currentIndex = 0;
  /**
   * The executor function responsible for invoking each middleware handler.
   */
  #executor;
  /**
   * The final handler to execute when the middleware chain completes successfully.
   */
  #finalHandler = DEFAULT_FINAL_HANDLER;
  /**
   * Optional error handler to catch and handle exceptions in the middleware pipeline.
   */
  #errorHandler;
  constructor(middleware) {
    this.#middleware = middleware;
  }
  /**
   * Invokes middleware handlers one at a time.
   *
   * Middleware functions are executed recursively until `next` is called.
   * If a middleware doesn't call `next`, the chain will finish automatically
   * without executing remaining handlers.
   *
   * @param self - The Runner instance scope
   * @returns A Promise that resolves when the middleware chain completes
   * @internal
   */
  #invoke(self) {
    const middleware = self.#middleware[self.#currentIndex++];
    if (!middleware) {
      return self.#finalHandler();
    }
    return self.#executor(middleware, once(self, self.#invoke));
  }
  /**
   * Invokes middleware handlers with error handling.
   *
   * Similar to `#invoke`, but catches exceptions and passes them to the error handler.
   * When an exception is raised, the middleware downstream logic will not run unless
   * the error handler allows the chain to continue.
   *
   * @param self - The Runner instance scope
   * @returns A Promise that resolves when the middleware chain completes
   * @internal
   */
  #invokeWithErrorManagement(self) {
    const middleware = self.#middleware[self.#currentIndex++];
    if (!middleware) {
      return self.#finalHandler().catch(self.#errorHandler);
    }
    return self.#executor(middleware, once(self, self.#invokeWithErrorManagement)).catch(self.#errorHandler);
  }
  /**
   * Sets a custom final handler to execute when the middleware chain completes successfully.
   *
   * The final handler is called when the entire middleware pipeline reaches the end
   * by calling `next` through all handlers. This makes it easier to execute custom
   * logic that is not part of the chain but must run when the chain ends.
   *
   * @param finalHandler - The function to execute when the chain completes
   * @returns The Runner instance for method chaining
   *
   * @example
   * ```ts
   * await middleware
   *   .runner()
   *   .finalHandler(() => {
   *     console.log('All middleware completed')
   *   })
   *   .run((fn, next) => fn(context, next))
   * ```
   */
  finalHandler(finalHandler) {
    this.#finalHandler = finalHandler;
    return this;
  }
  /**
   * Sets a custom error handler to catch exceptions in the middleware pipeline.
   *
   * By default, exceptions raised in the middleware pipeline bubble up to the `run`
   * method and can be captured using a try/catch block. When an exception is raised,
   * the middleware downstream logic will not run.
   *
   * Defining an error handler changes this behavior:
   * - The `run` method will not throw exceptions
   * - Errors are caught and passed to the error handler
   * - Middleware upstream logic (after `next`) can still execute
   *
   * @param errorHandler - The function to handle errors
   * @returns The Runner instance for method chaining
   *
   * @example
   * ```ts
   * await middleware
   *   .runner()
   *   .errorHandler((error) => {
   *     console.error('Middleware error:', error)
   *   })
   *   .run((fn, next) => fn(context, next))
   * ```
   */
  errorHandler(errorHandler) {
    this.#errorHandler = errorHandler;
    return this;
  }
  /**
   * Executes the middleware pipeline using the provided executor function.
   *
   * The executor function is responsible for invoking each middleware handler with
   * the appropriate context and the `next` callback. Since you control the executor,
   * you can pass any data you want to the middleware.
   *
   * @param cb - The executor function that invokes each middleware handler
   * @returns A Promise that resolves when the middleware pipeline completes
   *
   * @example
   * ```ts
   * const context = { user: null }
   * type MiddlewareFn = (ctx: typeof context, next: NextFn) => void | Promise<void>
   *
   * const middleware = new Middleware<MiddlewareFn>()
   * middleware.add(async (ctx, next) => {
   *   ctx.user = await authenticate()
   *   await next()
   * })
   *
   * await middleware
   *   .runner()
   *   .run((fn, next) => fn(context, next))
   * ```
   */
  async run(cb) {
    this.#executor = cb;
    if (this.#errorHandler) {
      return this.#invokeWithErrorManagement(this);
    }
    return this.#invoke(this);
  }
}
class Middleware {
  #middleware = /* @__PURE__ */ new Set();
  #middlewareArray;
  #isFrozen = false;
  /**
   * Returns all registered middleware handlers.
   *
   * @returns A Set containing all registered middleware handlers
   */
  all() {
    return this.#middleware;
  }
  /**
   * Checks if a specific handler has already been registered as middleware.
   *
   * @param handler - The middleware handler to check for
   * @returns `true` if the handler is registered, `false` otherwise
   */
  has(handler) {
    return this.#middleware.has(handler);
  }
  /**
   * Registers a new middleware handler to the pipeline.
   *
   * Adding the same middleware handler multiple times will result in a no-op,
   * as handlers are stored in a Set to prevent duplicates.
   *
   * @param handler - The middleware handler to register
   * @returns The Middleware instance for method chaining
   * @throws {Error} If the middleware stack is frozen
   */
  add(handler) {
    if (this.#isFrozen) {
      throw new Error("Middleware stack is frozen. Cannot add new middleware");
    }
    this.#middleware.add(handler);
    return this;
  }
  /**
   * Removes a specific middleware handler from the pipeline.
   *
   * @param handler - The middleware handler to remove
   * @returns `true` if the handler was removed, `false` if it was not found
   * @throws {Error} If the middleware stack is frozen
   */
  remove(handler) {
    if (this.#isFrozen) {
      throw new Error("Middleware stack is frozen. Cannot remove middleware");
    }
    return this.#middleware.delete(handler);
  }
  /**
   * Removes all registered middleware handlers from the pipeline.
   *
   * @throws {Error} If the middleware stack is frozen
   */
  clear() {
    if (this.#isFrozen) {
      throw new Error("Middleware stack is frozen. Cannot clear middleware");
    }
    this.#middleware.clear();
  }
  /**
   * Merges middleware handlers from another Middleware instance.
   *
   * The middleware from the source instance are appended to the current instance.
   *
   * @param hooks - The source Middleware instance to merge from
   * @throws {Error} If the middleware stack is frozen
   */
  merge(hooks) {
    if (this.#isFrozen) {
      throw new Error("Middleware stack is frozen. Cannot merge middleware");
    }
    hooks.all().forEach((handler) => {
      this.add(handler);
    });
  }
  /**
   * Freezes the middleware stack to prevent further modifications.
   *
   * Once frozen, the middleware array is cached and no new handlers can be added,
   * removed, or modified. This method is automatically called when creating a runner.
   */
  freeze() {
    if (this.#isFrozen) {
      return;
    }
    this.#isFrozen = true;
    this.#middlewareArray = [...this.all()];
  }
  /**
   * Creates and returns a Runner instance to execute the middleware pipeline.
   *
   * This method automatically freezes the middleware stack to prevent modifications
   * during execution.
   *
   * @returns A new Runner instance configured with the current middleware handlers
   */
  runner() {
    this.freeze();
    return new Runner(this.#middlewareArray);
  }
}
const version = "1.20251213.1";
export {
  Middleware,
  Runner,
  version
};
//# sourceMappingURL=index.mjs.map
